import React from 'react';
import useWarmupRoutinePlayer from './useWarmupRoutinePlayer';
import WarmupSetup from './WarmupSetup';
import WarmupRoutineList from './WarmupRoutineList';
import WarmupConsole from './WarmupConsole';
import { DEFAULT_CELL } from './warmupFormat';
import './WarmupPlayer.css';

export { DEFAULT_CELL };

/**
 * The warmup player — a routine, played.
 *
 * Designed for the Mac screen teachers teach on (Anthony, 2026-09-03: "we
 * primarily teach on desktop"), responsive below 768px rather than mobile-first.
 * Left: the routine as rows; right: the key readout and the transport. The
 * engine (`useWarmupPlayer` → `warmupSampler`) is passed in so one page shares
 * one AudioContext between this and the catalog rows' Play buttons.
 *
 * SINCE 2026-09-04 THIS COMPONENT IS A COMPOSER, NOT THE IMPLEMENTATION.
 * Lesson Dashboard I1 needs the deck in a sticky conducting strip under the
 * lesson bar and the routine in the right column — different parts of the tree
 * — so phase 0 split the player into a brain and three regions and left the
 * markup alone:
 *
 *   useWarmupRoutinePlayer  routine state, transport, the reached-top tracker
 *   WarmupSetup             draft header · last-lesson line · settings · alerts
 *   WarmupRoutineList       the rows  (.wp__list)
 *   WarmupConsole           readout + transport  (.wp__deck)
 *
 * The DOM this renders is IDENTICAL to what it rendered before the split —
 * same elements, same order, same classes — which is the whole point of doing
 * it as its own phase. A surface that wants the regions apart (I1) composes
 * them itself; a surface that wants the player whole (the Warmups page, and the
 * Lesson Dashboard until I1 phase 1 lands) uses this.
 *
 * TWO MODES OF OWNERSHIP, both now handled by the hook:
 *   - UNCONTROLLED (the Warmups page): the hook owns cell, routine and current.
 *   - CONTROLLED (the Lesson Dashboard): `routine`, `current` and their setters
 *     come from `useWarmupRoutine`, with a `draft` rendered as the "Unsaved
 *     changes" header and `onExerciseStart` / `onExerciseEnd` for the log.
 *
 * WHAT THE TEACHER CAN DO, and where the behaviour lives:
 *   - pick duration / level / age band / style / voice  → assembleRoutine
 *   - Swap or Skip an exercise (the pedagogy holds)      → swapCandidates / swapExercise / removeExercise
 *   - Play / Pause · Rewind · Back · Next (keys)          → engine transport
 *   - Transpose ± · Drop / raise an octave · Descend       → the walk reducer
 *   - Tempo · Guide full / low / off                       → engine gains
 * The walk HOLDS at the top until Descend, Drop octave or Next (Anthony's
 * chosen default). An exercise that finishes its descent auto-advances to the
 * next row after the generator's rest bar; the last row ends the routine.
 *
 * EVERY CONTROL IS ALWAYS VISIBLE. No hover-only affordances (CLAUDE.md), and
 * every transport button is at least 44px. `compact` (the lesson's right
 * column, 320px) stacks the deck under the rows and tucks the settings behind
 * a disclosure — same controls, narrower.
 */
const WarmupPlayer = ({
	catalog,
	player,
	initialCell = DEFAULT_CELL,
	// controlled mode (all optional; provide together)
	routine: routineProp = null,
	onRoutineChange = null,
	onCellChange = null,
	current: currentProp = null,
	onCurrentChange = null,
	draft = null,
	onExerciseStart = null,
	onExerciseEnd = null,
	headerLine = '',
	compact = false,
}) => {
	const wp = useWarmupRoutinePlayer({
		catalog,
		player,
		initialCell,
		routine: routineProp,
		onRoutineChange,
		onCellChange,
		current: currentProp,
		onCurrentChange,
		onExerciseStart,
		onExerciseEnd,
	});

	return (
		<section className={`wp${compact ? ' wp--compact' : ''}`} aria-label="Warmup player">
			<WarmupSetup wp={wp} draft={draft} headerLine={headerLine} compact={compact} />

			<div className="wp__body">
				<WarmupRoutineList wp={wp} compact={compact} />
				<WarmupConsole wp={wp} />
			</div>
		</section>
	);
};

export default WarmupPlayer;
